// analytics/hooks/useClinicalData.ts — SKIN-35
import { useState, useEffect } from 'react';
import axios from 'axios';
import { clinicalServices } from '../services/endpoints/clinical';
import { PatientsWithoutImagesRow } from '../PatientsWithoutImages';

export const useClinicalData = (investigation: { uuid: string } | undefined) => {
    const [data, setData] = useState<PatientsWithoutImagesRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    // Bumped to force a refetch on retry.
    const [reloadToken, setReloadToken] = useState(0);

    const retry = () => setReloadToken(t => t + 1);

    useEffect(() => {
        // Cancels the XHR for real and marks this run superseded — see the fuller note in
        // useDermatologistActivity and the mechanism note in the service (SKIN-260).
        const source = axios.CancelToken.source();
        const superseded = () => Boolean(source.token.reason);

        const fetchData = async () => {
            if (!investigation?.uuid) return;
            setLoading(true);
            setError(null);
            try {
                const rows = await clinicalServices.getVisitsWithoutImages(investigation.uuid, source.token);
                if (superseded()) return;
                setData(rows);
            } catch (err) {
                // A cancellation is not a failure; retry() and unmount both cancel.
                if (axios.isCancel(err) || superseded()) return;
                setError(err instanceof Error ? err : new Error('Unknown error'));
            } finally {
                if (!superseded()) setLoading(false);
            }
        };

        fetchData();
        return () => source.cancel('superseded');
    }, [investigation?.uuid, reloadToken]);

    return { data, loading, error, retry };
};
